/**
 * Message interpolation and plurals.
 *
 * WHAT THIS IS FOR
 * ----------------
 * The module's strings all live in `constants/strings.ts`, which is the hard
 * part and was already done. What was missing is that several of them are
 * *assembled in JSX*:
 *
 *     `${STRINGS.pane.ctaWidth} — ${cta.widthPct}%`
 *     `(${product.reviewCount} reviews)`
 *     `#${product.bestSellerRank} Best Seller`
 *     `Price per unit ${formatPrice(product.pricePerUnit)}`
 *
 * Each of those hardcodes English word order into a component. A translator
 * cannot move the number to the other side of the noun, cannot change the
 * separator, and cannot supply the four plural forms Welsh needs — because the
 * sentence does not exist anywhere as a sentence. Putting the placeholders *in*
 * the message is what makes the string translatable at all.
 *
 * WHY NOT AN i18n LIBRARY
 * -----------------------
 * `react-intl` or `i18next` would be the answer for an application-wide rollout,
 * and this module is one screen in an application that has no i18n runtime at
 * all. Adding a peer-dependency-heavy framework for one module would be a
 * decision taken on the whole app's behalf from inside a corner of it. This is
 * ~40 lines with the same message *shape* — `{placeholder}` and a plural
 * category map — so the day the app adopts one, these catalogues port across
 * rather than needing to be rewritten.
 */

import React, { ReactNode } from 'react';

import { resolveLocale } from './locale';

export type MessageValues = Record<string, string | number>;

/**
 * `'Button width — {width}%'` + `{ width: 60 }` → `'Button width — 60%'`.
 *
 * An unmatched placeholder is left standing rather than replaced with an empty
 * string. `'Showing {count} of {total}'` rendering as `'Showing 5 of {total}'`
 * is an obvious bug in a screenshot; `'Showing 5 of '` reads like a design
 * choice and ships.
 */
export const interpolate = (template: string, values: MessageValues = {}): string =>
  template.replace(/\{(\w+)\}/g, (placeholder, name: string) =>
    Object.prototype.hasOwnProperty.call(values, name) ? String(values[name]) : placeholder
  );

/**
 * The CLDR plural categories a locale can use. `other` is the only one every
 * locale has, which is why it is the required member below.
 */
export interface PluralForms {
  zero?: string;
  one?: string;
  two?: string;
  few?: string;
  many?: string;
  other: string;
}

/**
 * Picks the right form for `count` in the active locale, then interpolates.
 *
 * The category comes from `Intl.PluralRules`, not from `count === 1`. English
 * has two forms and that shortcut works; Arabic has six, Polish three, Japanese
 * one. Hand-rolling the rule is how "1 reviews" ships — and it is the reason
 * this takes a form *map* rather than a singular and a plural.
 *
 * A locale asking for a category the catalogue does not define falls back to
 * `other`, which every locale defines and every catalogue must supply.
 */
export const plural = (
  count: number,
  forms: PluralForms,
  values: MessageValues = {},
  locale: string = resolveLocale()
): string => {
  let category: Intl.LDMLPluralRule = 'other';
  try {
    category = new Intl.PluralRules(locale).select(count);
  } catch {
    // An unusable locale tag must not break a review count.
  }
  const template = forms[category] ?? forms.other;
  return interpolate(template, { count, ...values });
};

/**
 * `interpolate` for messages whose values are RENDERED, not stringified.
 *
 * `'{cardName} is currently being used by {applications}.'` + a `<Typography>`
 * and a list of `<Tag>`s comes back as an array React renders as one flowing
 * sentence. `interpolate` cannot do this: `String(<Tag/>)` is `[object Object]`.
 *
 * The reason it exists rather than the component assembling the sentence from
 * fragments is the same reason `interpolate` does — with the name, the verb
 * phrase and the badges as three sibling nodes in JSX, English word order and
 * the position of the full stop live in the component, where no translator can
 * reach them. A locale that says "Resolve uses {cardName}" only needs to move
 * the placeholders in the catalogue.
 *
 * Unmatched placeholders are left standing, exactly as `interpolate` leaves
 * them, and for the same reason.
 */
export const interpolateNodes = (
  template: string,
  values: Record<string, ReactNode> = {}
): ReactNode[] =>
  template
    // The capturing group keeps the delimiters, so literals and placeholders
    // come back in one ordered list.
    .split(/(\{\w+\})/g)
    .filter((segment) => segment !== '')
    .map((segment, index) => {
      const name = /^\{(\w+)\}$/.exec(segment)?.[1];
      if (name === undefined || !Object.prototype.hasOwnProperty.call(values, name)) {
        return segment;
      }
      /* A Fragment because the value may be a list of nodes, and because a keyed
         wrapper is what lets an array child avoid React's key warning without
         the caller keying values it did not choose the order of. */
      return React.createElement(React.Fragment, { key: `${name}-${index}` }, values[name]);
    });
